import { useEffect } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { client } from '@trello/graphql';
import { getScreenFromUrl } from '@trello/marketing-screens';
import { useQuickLoad } from '@trello/quickload';

import type { MemberAvatarFallbackQuery } from './MemberAvatarFallbackQuery.generated';
import {
  MemberAvatarFallbackDocument,
  useMemberAvatarFallbackLazyQuery,
} from './MemberAvatarFallbackQuery.generated';
import type { MemberAvatarFragment } from './MemberAvatarFragment.generated';
import { useMemberAvatarFragment } from './MemberAvatarFragment.generated';

/**
 * Temporary workaround to help us transition from using queries to fragments
 * for the member avatar component. Ultimately, we _never_ want to query data
 * for this component, and always read from cache. In practice, though, we're
 * still seeing some number of requests being made for this component, which
 * means those experiences would be broken for fragments.
 *
 * This hook always reads fragment data from cache, and tries to return it.
 * IFF the fragment data is empty and the `fep.use-fragments-for-member-avatar`
 * feature flag is _not_ enabled, it will attempt to read the query, which may
 * result in a network request. We'll track these query reads over time to
 * identify where our cache misses are, which should allow us to remove the
 * query eventually.
 */
export const useMemberAvatarData = ({
  memberId,
  overrideData,
  testId,
}: {
  memberId: string;
  overrideData?: MemberAvatarFragment;
  testId?: string;
}): MemberAvatarFragment | undefined => {
  // NOTE: This flag will be rolled out incrementally as normal, but defaults to
  // true mainly for compatibility with the new unit tests and Storybook.
  // Tests didn't exist for this component at all before this hook.
  const shouldRelyOnFragmentData = useFeatureFlag<boolean>(
    'fep.use-fragments-for-member-avatar',
    true,
  );

  const { data: fragmentData } = useMemberAvatarFragment({
    from: { id: memberId },
    returnPartialData: true,
    optimistic: true,
  });

  const isQuickLoading = useQuickLoad({
    waitOn: ['MemberHeader'],
    document: MemberAvatarFallbackDocument,
    skip: shouldRelyOnFragmentData,
  });
  const [queryData, { data: queriedData }] = useMemberAvatarFallbackLazyQuery({
    variables: { memberId },
  });
  useEffect(() => {
    if (shouldRelyOnFragmentData || isQuickLoading) return;
    if (overrideData || fragmentData) return;

    requestAnimationFrame(() => {
      let missingErrors: Error[] | undefined;
      try {
        const diff = client.cache.diff<MemberAvatarFallbackQuery>({
          query: MemberAvatarFallbackDocument,
          variables: { memberId },
          returnPartialData: true,
          optimistic: true,
        });
        missingErrors = diff?.missing;
      } catch (e) {
        // noop
      }

      queryData();

      Analytics.sendOperationalEvent({
        action: 'queried',
        actionSubject: 'fetchMemberAvatarFallback',
        // For now, if this event fires, let's try to identify where it happened.
        source: getScreenFromUrl(),
        attributes: { missingErrors, testId },
      });
    });
  }, [
    fragmentData,
    isQuickLoading,
    memberId,
    overrideData,
    queryData,
    shouldRelyOnFragmentData,
    testId,
  ]);

  if (overrideData) {
    return overrideData;
  }
  if (shouldRelyOnFragmentData) {
    return fragmentData;
  }
  // Fragment data should be populated once the query is made, so storing this
  // in its own state is pretty redundant, but I think it makes the logic clear.
  return fragmentData || (queriedData?.member ?? undefined);
};
